import { type MenuInfo } from "rc-menu/lib/interface";
import { createRef, PureComponent, type SyntheticEvent } from "react";

import { BTDropdown } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { handleEnterKeyPressed } from "utilities/form/form";
import { addCloseOnScroll, getClosestModal, removeCloseOnScroll } from "utilities/helpers";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconActions, BTIconDeleteFilled } from "commonComponents/btWrappers/BTIcon";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemRow } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IBaseLineItem,
    LineItemColumns,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import "./ActionCell.less";

export interface IActionCellProps {
    actions?: IActionCellAction[];
    deleteAction?: IActionCellDeleteAction;
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    onDropdownClick?: () => void;
    actionHoverText?: string;
}

export interface IActionCellAction {
    text: string;
    onClick: (
        event:
            | React.KeyboardEvent<HTMLElement>
            | React.MouseEvent<HTMLElement, MouseEvent>
            | SyntheticEvent,
    ) => Promise<void>;
    disabled?: boolean;
    className?: string;
    field?: string;
    tooltip?: string;
}

export interface IActionCellDeleteAction extends IActionCellAction {
    isInEdit: boolean;
}

interface IActionCellState {
    open: boolean;
}

export class ActionCell extends PureComponent<
    IGridCellRenderProps<IActionCellProps>,
    IActionCellState
> {
    state: IActionCellState = {
        open: false,
    };

    dropdownMenuSpan = createRef<HTMLDivElement>();

    private handleEnterKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
        const { cellValue } = this.props;

        if (cellValue?.deleteAction?.onClick) {
            handleEnterKeyPressed(e, () => cellValue.deleteAction?.onClick(e));
        }
    };

    private handleMenuDeleteClick = async (menuInfo: MenuInfo) => {
        await this.handleDeleteClick(menuInfo.domEvent);
    };

    private handleDeleteClick = async (
        event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement, MouseEvent>,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        const { cellValue } = this.props;

        if (cellValue?.deleteAction) {
            await cellValue.deleteAction.onClick(event);
        }
    };

    private handleVisibleChange = (open: boolean) => {
        this.setState({ open: open });

        if (this.dropdownMenuSpan.current) {
            if (open) {
                addCloseOnScroll(this.dropdownMenuSpan.current, this.handleScroll);
            } else {
                removeCloseOnScroll(this.dropdownMenuSpan.current, this.handleScroll);
            }
        }

        if (this.props.cellValue?.onDropdownClick) {
            this.props.cellValue.onDropdownClick();
        }
    };

    private handleScroll = () => {
        // close on scroll
        this.handleVisibleChange(false);
    };

    render() {
        const { cellValue, field } = this.props;

        if (!cellValue) {
            return null;
        }

        if (cellValue.actions && cellValue.actions.length !== 0) {
            const menu = (
                <BTMenu testid="actionCellMenu">
                    {cellValue.actions.map((action) => (
                        <BTMenu.Item
                            testid={(action.field ?? field.full) + action.text}
                            key={field.full + action.text}
                            onClick={(menu) => action.onClick(menu.domEvent)}
                            onKeyDown={this.handleEnterKeyDown}
                            disabled={action.disabled}
                            tabIndex={0}
                        >
                            {action.tooltip ? (
                                <BTPopover content={action.tooltip}>{action.text}</BTPopover>
                            ) : (
                                action.text
                            )}
                        </BTMenu.Item>
                    ))}
                    {cellValue.deleteAction && (
                        <BTMenu.Item
                            testid={`${cellValue.deleteAction.field ?? field.full}-delete`}
                            key={`${cellValue.deleteAction.field ?? field.full}-delete`}
                            onClick={this.handleMenuDeleteClick}
                            onKeyDown={this.handleEnterKeyDown}
                            disabled={cellValue.deleteAction.disabled}
                            tabIndex={0}
                        >
                            {cellValue.deleteAction.text}
                        </BTMenu.Item>
                    )}
                </BTMenu>
            );

            return (
                <>
                    <span ref={this.dropdownMenuSpan} />
                    <BTDropdown
                        testid={`action-cell-dropdown-${field.full}`}
                        overlay={menu}
                        getPopupContainer={cellValue.getPopupContainer ?? getClosestModal}
                        onVisibleChange={this.handleVisibleChange}
                        visible={this.state.open}
                        placement="bottomRight"
                    >
                        <BTPopover
                            overlayClassName="ActionCellPopover"
                            content={cellValue.actionHoverText ?? "More"}
                        >
                            <BTButtonDeprecated
                                type="link"
                                data-testid={field.full}
                                icon={<BTIconActions />}
                                tabIndex={0}
                            />
                        </BTPopover>
                    </BTDropdown>
                </>
            );
        }

        if (cellValue.deleteAction) {
            return (
                <div className="ActionCell">
                    <BTPopover content={cellValue.deleteAction.tooltip || "Delete"}>
                        <BTButtonDeprecated
                            type="link"
                            data-testid={field.full}
                            onClick={this.handleDeleteClick}
                            icon={<BTIconDeleteFilled />}
                            tabIndex={0}
                            disabled={cellValue.deleteAction.disabled}
                            className={cn(cellValue.deleteAction.className, {
                                EditMode: cellValue.deleteAction.isInEdit,
                            })}
                        />
                    </BTPopover>
                </div>
            );
        }

        return null;
    }
}

export const deleteActionCellDefaultConfig = <T extends IBaseLineItem & ILineItemRow>(
    onDelete?: (lineItem: T) => Promise<void>,
) => ({
    id: LineItemColumns.DeleteAction,
    name: "",
    jsonKey: "action",
    width: 40,
    minWidth: 48,
    ellipsis: true,
    align: "center",
    isSortable: false,
    columnType: GridLineItemColumnType.action,
    isFrozen: true,
    isHidden: false,
    className: "entity-action-column",
    getCellValue: onDelete
        ? (row: T): IActionCellProps => ({
              deleteAction: {
                  onClick: async () => {
                      await onDelete(row);
                  },
                  text: "Delete",
                  className: "DeleteIcon",
                  isInEdit: row._isInEdit ?? false,
              },
          })
        : undefined,
    Cell: ActionCell,
});
